import { currentCountry, instanceName } from "@/jurisdictions/country";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(null); // 2FA connexion
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const attempt = () => api.post("/auth/login", form);
    try {
      let data;
      try {
        ({ data } = await attempt());
      } catch (err1) {
        const st = err1?.response?.status;
        if (!err1?.response || [502, 503, 504].includes(st)) {
          await new Promise((r) => setTimeout(r, 1200));
          ({ data } = await attempt());
        } else {
          throw err1;
        }
      }
      if (data.otp_required) {
        setOtp({ otp_id: data.otp_id, email_masked: data.email_masked });
        setCode("");
        toast.success(`Un code de vérification a été envoyé à ${data.email_masked}.`);
        setLoading(false);
        return;
      }
      await login(data.token, data.user);
      toast.success(`Bienvenue, ${data.user.name}`);
      navigate(data.user.role === "client" ? "/espace-client" : "/app");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { otp_id: otp.otp_id, code });
      await login(data.token, data.user);
      toast.success(`Bienvenue, ${data.user.name}`);
      navigate(data.user.role === "client" ? "/espace-client" : "/app");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const forgot = async () => {
    const email = window.prompt("Entrez votre email pour recevoir un lien de réinitialisation sécurisé :", form.email);
    if (!email) return;
    try {
      await api.post("/auth/forgot-password", { email });
    } catch (e) { /* no user enumeration */ }
    toast.success("Si un compte existe, un lien de réinitialisation vient d'être envoyé par email.");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left — brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 overflow-hidden">
        <div className="absolute inset-0 fidalis-grid-bg opacity-[0.08]" />
        <div className="relative flex items-center gap-3">
          <Logo className="h-11 w-11" />
          <div>
            <div className="font-heading font-extrabold text-2xl tracking-tight">Ninyaa</div>
            <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Expertise · Audit · Conseil</div>
          </div>
        </div>

        <div className="relative max-w-md">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-primary-foreground/10 px-3 py-1.5 rounded-full mb-6">
            <Sparkle size={13} weight="fill" /> {instanceName()}
          </div>
          <h1 className="font-heading font-black text-4xl xl:text-5xl leading-[1.05] tracking-tight">
            Votre cabinet, votre espace de travail.
          </h1>
          <p className="mt-6 text-primary-foreground/80 leading-relaxed">
            {currentCountry() === "FR" ? "Retrouvez vos dossiers, préparez vos travaux et suivez vos échéances. Tout ce dont votre équipe a besoin, au même endroit." : "Votre copilote et vos dossiers d’expertise comptable au Maroc : comptabilité, fiscalité, paie et suivi du cabinet. L’accès est rattaché à votre cabinet."}
          </p>
        </div>

        <div className="relative grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-lg font-semibold">Dossiers</div>
            <div className="text-xs opacity-70">vos clients et leurs pièces</div>
          </div>
          <div>
            <div className="text-lg font-semibold">{currentCountry() === "FR" ? "Travaux" : "MAD"}</div>
            <div className="text-xs opacity-70">de la saisie à la révision</div>
          </div>
          <div>
            <div className="text-lg font-semibold">{currentCountry() === "FR" ? "Équipe" : "CGNC"}</div>
            <div className="text-xs opacity-70">un suivi partagé</div>
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <Logo className="h-9 w-9" />
            <div>
              <span className="font-heading font-extrabold text-xl block leading-none">{instanceName()}</span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Expertise · Audit · Conseil</span>
            </div>
          </div>

          <h2 className="font-heading font-bold text-2xl tracking-tight">
            {otp ? "Vérification en deux étapes" : "Connexion au cabinet"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1 mb-8">
            {otp ? `Saisissez le code envoyé à ${otp.email_masked}.` : "Accès sur invitation de votre administrateur de cabinet."}
          </p>

          {otp ? (
            <form onSubmit={verifyOtp} className="space-y-4" data-testid="otp-form">
              <div className="space-y-2">
                <Label htmlFor="login-otp">Code de vérification (ou code de secours)</Label>
                <Input id="login-otp" autoComplete="one-time-code" data-testid="otp-code-input" value={code}
                  onChange={(e) => setCode(e.target.value)} placeholder="123456 ou xxxxxx-xxxxxx"
                  className="tracking-[0.3em] text-center text-lg font-mono" />
              </div>
              <Button type="submit" disabled={loading || code.length < 6} data-testid="otp-submit-button" className="w-full">
                {loading ? "Vérification…" : "Vérifier et se connecter"}
              </Button>
              <button type="button" onClick={() => { setOtp(null); setCode(""); }} data-testid="otp-cancel"
                className="w-full text-xs text-muted-foreground hover:text-foreground">← Revenir à la connexion</button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email professionnel</Label>
              <Input
                id="email"
                type="email" autoComplete="username"
                data-testid="login-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                placeholder="vous@cabinet.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password" autoComplete="current-password"
                data-testid="login-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                placeholder="••••••••"
              />
            </div>

            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full group">
              {loading ? "Veuillez patienter…" : "Se connecter"}
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>
          )}

          {!otp && (
            <button
              type="button"
              onClick={forgot}
              data-testid="forgot-password-link"
              className="mt-2 w-full text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Mot de passe oublié ?
            </button>
          )}

          {!otp && <p className="mt-6 text-center text-xs text-muted-foreground" data-testid="invitation-only-notice">
            Pour obtenir un accès ou une nouvelle invitation, contactez l’administrateur de votre cabinet.
          </p>}
        </div>
        <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-muted-foreground" data-testid="login-legal-footer">
          <Link to="/mentions-legales" className="hover:text-primary transition-colors" data-testid="footer-mentions">Mentions légales</Link>
          <span className="opacity-40">·</span>
          <Link to="/confidentialite" className="hover:text-primary transition-colors" data-testid="footer-privacy">Confidentialité</Link>
        </div>

      </div>
    </div>
  );
}
